import { findReferenceList } from "@/lib/tool-kit/text-analysis/sections";
import {
  collapseWhitespace,
  countWords,
  splitParagraphs,
  type TextSpan,
} from "@/lib/tool-kit/text-analysis/segment";

/**
 * Quoted material in a draft: run-in quotations in double quotation marks (straight or
 * curly), British-style single quotation marks on request, and paragraphs the writer
 * has marked as block quotations.
 *
 * A quotation never crosses a paragraph boundary. That is a deliberate limit: with
 * straight quotes, one missing closing mark would otherwise flip every later quotation
 * inside-out. Contained to its paragraph, a typo costs one warning.
 */

export interface QuotationOptions {
  /** Treat ‘single’ quotation marks as quotations too (British style). */
  singleQuotes?: boolean;
  /** Treat paragraphs that start with ">" as block quotations. */
  blockMarker?: boolean;
  /** Only look before this offset (the reference list starts here). */
  limit?: number;
}

export interface Quotation extends TextSpan {
  /** The quoted words, without the marks (or the ">" of a block quotation). */
  innerStart: number;
  innerEnd: number;
  words: number;
  /** Characters inside the marks with whitespace runs counted once. */
  characters: number;
  block: boolean;
}

export interface QuotationScan {
  quotations: Quotation[];
  /** Offsets of quotation marks with no partner in their paragraph. */
  unmatched: number[];
}

const DOUBLE_OPEN = new Set(["“", "«"]);
const DOUBLE_CLOSE = new Set(["”", "»"]);
const STRAIGHT_DOUBLE = '"';
const SINGLE_OPEN = "‘";
const SINGLE_CLOSE = "’";
const STRAIGHT_SINGLE = "'";
const OPENING_CONTEXT_RE = /[\s([{—–/-]/u;
const WORD_CHAR_RE = /[\p{L}\p{N}]/u;
const SPACE_RE = /\s/;
const DIGIT_RE = /\d/;
const PUNCT_AFTER_RE = /[.,;:!?)\]—–-]/u;
const POSSESSIVE_S_RE = /[sS]/;
const BLOCK_LINE_MARKER_RE = /^[ \t]*>[ \t]?/gm;

/** A straight quotation mark opens when it follows a space or bracket and precedes text. */
function opensAt(text: string, i: number, start: number, end: number) {
  const before = i === start || OPENING_CONTEXT_RE.test(text[i - 1]);
  return before && i + 1 < end && !SPACE_RE.test(text[i + 1]);
}

function isDoubleOpener(text: string, i: number, span: TextSpan) {
  const c = text[i];
  return (
    DOUBLE_OPEN.has(c) ||
    (c === STRAIGHT_DOUBLE && opensAt(text, i, span.start, span.end))
  );
}

function isSingleOpener(text: string, i: number, span: TextSpan) {
  const c = text[i];
  if (c === SINGLE_OPEN) {
    return i + 1 < span.end && !SPACE_RE.test(text[i + 1]);
  }
  return (
    c === STRAIGHT_SINGLE &&
    opensAt(text, i, span.start, span.end) &&
    WORD_CHAR_RE.test(text[i + 1] ?? "")
  );
}

/** The first closing double mark, or -1 if another opening mark comes first. */
function findDoubleClose(text: string, from: number, end: number): number {
  for (let j = from; j < end; j++) {
    const c = text[j];
    if (DOUBLE_CLOSE.has(c) || c === STRAIGHT_DOUBLE) {
      return j;
    }
    if (DOUBLE_OPEN.has(c)) {
      return -1;
    }
  }
  return -1;
}

/** ’ or ' that is not inside a word (so not the apostrophe in "don't"). */
function isSingleCloseCandidate(text: string, j: number, end: number) {
  const c = text[j];
  if (c !== SINGLE_CLOSE && c !== STRAIGHT_SINGLE) {
    return false;
  }
  const before = text[j - 1] ?? " ";
  if (SPACE_RE.test(before)) {
    return false;
  }
  return j + 1 >= end || !WORD_CHAR_RE.test(text[j + 1]);
}

/**
 * "the participants’ answers" — a plural possessive looks exactly like a closing single
 * quotation mark: an s, the mark, a space, a word.
 */
function isPossessiveLike(text: string, j: number, end: number) {
  if (j + 1 >= end) {
    return false;
  }
  const after = text[j + 1];
  return (
    POSSESSIVE_S_RE.test(text[j - 1]) &&
    SPACE_RE.test(after) &&
    WORD_CHAR_RE.test(text[j + 2] ?? "") &&
    !PUNCT_AFTER_RE.test(after)
  );
}

/**
 * The closing single mark for a quotation opened just before `from`.
 *
 * Single marks double as apostrophes, so every candidate up to the next opening mark is
 * collected first. The first one that is not shaped like a plural possessive closes the
 * quotation; if every candidate looks possessive, the last one does. A quotation nested
 * in double marks is skipped whole.
 */
function findSingleClose(text: string, from: number, span: TextSpan) {
  const candidates: number[] = [];
  let j = from;
  while (j < span.end) {
    if (isDoubleOpener(text, j, span)) {
      const nested = findDoubleClose(text, j + 1, span.end);
      j = Math.max(nested, j) + 1;
      continue;
    }
    if (isSingleOpener(text, j, span)) {
      break;
    }
    if (isSingleCloseCandidate(text, j, span.end)) {
      candidates.push(j);
    }
    j++;
  }
  const clear = candidates.find((c) => !isPossessiveLike(text, c, span.end));
  return clear ?? candidates.at(-1) ?? -1;
}

function inlineQuotation(text: string, open: number, close: number): Quotation {
  const inner = text.slice(open + 1, close);
  return {
    start: open,
    end: close + 1,
    innerStart: open + 1,
    innerEnd: close,
    words: countWords(inner),
    characters: collapseWhitespace(inner).length,
    block: false,
  };
}

function blockQuotation(text: string, span: TextSpan): Quotation {
  const inner = text.slice(span.start + 1, span.end);
  const plain = inner.replace(BLOCK_LINE_MARKER_RE, "");
  return {
    start: span.start,
    end: span.end,
    innerStart: span.start + 1,
    innerEnd: span.end,
    words: countWords(plain),
    characters: collapseWhitespace(plain).length,
    block: true,
  };
}

function scanParagraph(
  text: string,
  span: TextSpan,
  singleQuotes: boolean,
  scan: QuotationScan
) {
  let i = span.start;
  while (i < span.end) {
    let close = -2;
    if (isDoubleOpener(text, i, span)) {
      close = findDoubleClose(text, i + 1, span.end);
    } else if (singleQuotes && isSingleOpener(text, i, span)) {
      close = findSingleClose(text, i + 1, span);
    }
    if (close >= 0) {
      scan.quotations.push(inlineQuotation(text, i, close));
      i = close + 1;
      continue;
    }
    if (close === -1 || isStrayCloser(text, i)) {
      scan.unmatched.push(i);
    }
    i++;
  }
}

/** A closing double mark with nothing open — except an inch mark (12" ruler). */
function isStrayCloser(text: string, i: number): boolean {
  const c = text[i];
  if (DOUBLE_CLOSE.has(c)) {
    return true;
  }
  return c === STRAIGHT_DOUBLE && !DIGIT_RE.test(text[i - 1] ?? "");
}

export function findQuotations(
  text: string,
  options: QuotationOptions = {}
): QuotationScan {
  const scan: QuotationScan = { quotations: [], unmatched: [] };
  const limit = options.limit ?? text.length;
  let previousBlock: Quotation | null = null;
  for (const paragraph of splitParagraphs(text)) {
    if (paragraph.start >= limit) {
      break;
    }
    const span = {
      start: paragraph.start,
      end: Math.min(paragraph.end, limit),
    };
    if (options.blockMarker && text[span.start] === ">") {
      // Consecutive marked paragraphs are one block quotation.
      if (previousBlock) {
        const merged = blockQuotation(text, {
          start: previousBlock.start,
          end: span.end,
        });
        scan.quotations[scan.quotations.length - 1] = merged;
        previousBlock = merged;
      } else {
        previousBlock = blockQuotation(text, span);
        scan.quotations.push(previousBlock);
      }
      continue;
    }
    previousBlock = null;
    scanParagraph(text, span, options.singleQuotes ?? false, scan);
  }
  return scan;
}

/* --------------------------------------------------------------------- report */

/** APA 7: a quotation of 40 words or more is set as a block quotation. */
export const APA_BLOCK_WORDS = 40;

/** MLA 9: a prose quotation that runs to more than four lines is set off as a block. */
export const MLA_BLOCK_LINES = 4;

/**
 * Characters on one typed line — 12-point Times New Roman across a 6.5-inch line (US
 * Letter, one-inch margins). An estimate: MLA counts the lines as they fall in your
 * paper, which depends on the typeface and on where the quotation starts.
 */
export const MLA_CHARS_PER_LINE = 90;

/** Quotations this short are often titles or scare quotes rather than borrowed prose. */
export const SHORT_QUOTATION_WORDS = 3;

export interface QuotationReportOptions {
  singleQuotes: boolean;
  blockMarker: boolean;
  ignoreReferences: boolean;
  ignoreShort: boolean;
}

export interface ReportedQuotation extends Quotation {
  /** Included in the quoted-word total. */
  counted: boolean;
  /** Estimated lines in a typed paper. */
  lines: number;
  /** Run-in, but APA would set it as a block (40+ words). */
  apaBlock: boolean;
  /** Run-in, but MLA would set it as a block (more than four lines). */
  mlaBlock: boolean;
}

export interface QuotationReport {
  totalWords: number;
  quotedWords: number;
  /** Quoted words as a share of all words, 0–100. */
  percent: number;
  quotations: ReportedQuotation[];
  unmatched: number[];
  /** The heading of the ignored reference list, when one was found and ignored. */
  referencesHeading: string | null;
}

export function quotationReport(
  text: string,
  options: QuotationReportOptions
): QuotationReport {
  const references = options.ignoreReferences ? findReferenceList(text) : null;
  const limit = references?.start ?? text.length;
  const scan = findQuotations(text, {
    singleQuotes: options.singleQuotes,
    blockMarker: options.blockMarker,
    limit,
  });
  const quotations = scan.quotations.map((quotation): ReportedQuotation => {
    const lines = quotation.characters / MLA_CHARS_PER_LINE;
    return {
      ...quotation,
      counted: !(
        options.ignoreShort &&
        !quotation.block &&
        quotation.words <= SHORT_QUOTATION_WORDS
      ),
      lines,
      apaBlock: !quotation.block && quotation.words >= APA_BLOCK_WORDS,
      mlaBlock: !quotation.block && lines > MLA_BLOCK_LINES,
    };
  });
  const totalWords = countWords(text.slice(0, limit));
  const quotedWords = quotations
    .filter((quotation) => quotation.counted)
    .reduce((sum, quotation) => sum + quotation.words, 0);
  return {
    totalWords,
    quotedWords,
    percent: totalWords > 0 ? (quotedWords / totalWords) * 100 : 0,
    quotations,
    unmatched: scan.unmatched,
    referencesHeading: references?.heading ?? null,
  };
}
